<template>
  <div
    class="houston-table"
    role="table"
    :aria-rowcount="rows.length"
    :style="{
      'grid-template-columns': `repeat(${computedColumnsCount}, auto)`,
    }"
    @mouseleave="stopSelecting"
  >
    <header role="row">
      <div
        v-if="!hideSelected"
        role="cell"
        class="houston-table__header-cell"
      />
      <div
        v-for="(column, index) in computedColumns"
        :key="`h-${index}`"
        role="cell"
        class="houston-table__header-cell"
      >
        <slot :name="`h-${column}`">
          {{ column }}
        </slot>
      </div>
    </header>
    <slot name="rows">
      <transition-group name="v-grow" tag="div" class="houston-table__body">
        <div
          v-for="(row, index) in rowsWithDrawer"
          :key="row.type === 'drawer' ? 'drawer' : `i-${row.index}`"
          :class="{ selected: row.selected }"
          role="row"
          @mousedown="startSelecting(row)"
          @mouseup="stopSelecting"
          @mousemove="selecting(row)"
        >
          <div
            v-if="row.type === 'drawer' && $slots.drawer"
            class="houston-table__drawer"
            :style="{ 'grid-column': `span ${computedColumnsCount}` }"
          >
            <slot name="drawer" />
          </div>
          <slot v-else :name="`r-${index}`">
            <div
              v-if="!hideSelected"
              role="cell"
              :style="`background-color: ${rowColorRules(row.item.status)}`"
            >
              <Icon :id="row.selected ? 'check' : 'uncheck'" />
            </div>
            <div
              v-for="(column, rowIndex) in computedColumns"
              :key="`r-${rowIndex}`"
              role="cell"
              :style="`background-color: ${rowColorRules(row.item.status)}`"
            >
              <slot :name="`c-${column}`" :index="index" :row="row">
                {{ row.item[column] }}
              </slot>
            </div>
          </slot>
        </div>
      </transition-group>
    </slot>
  </div>
</template>

<script>
/**
 * A row represents either an item of the table, or a drawer
 * when at least one regular row is selected.
 * @class
 */
class Row {
  /** @type {'regular'|'drawer'} Whether the row represents a regular item or the drawer */
  type = 'regular';
  /** @type {?boolean} Whether the row is selected */
  selected = false;
  /** @type {?any} The item that the row represents */
  item;
  /** @type {number} The index of the row */
  index;
  constructor(opts) {
    Object.assign(this, opts);
  }
}
/**
 * A DataTable is a grid container that mimicks
 * the behavior of a regular HTML table, although it
 * benefits from less rigid rules for its formatting
 * (which allows us to use sticky positions and max-height).
 *
 * A DataTable offers many dynamic slots to customize its content.
 * See the template code for more about this.
 */
export default {
  props: {
    /** @type {import('vue').PropOptions<any[]>} The list of items to display */
    items: { type: Array, required: true },
    /** @type {import('vue').PropOptions<string[]>} An optional list of column to use. If none, columns will be derived automatically (see below) */
    columns: { type: Array, default: null },
    /** @type {boolean} Whether the show the first column, with the selection status of the row */
    hideSelected: { type: Boolean, default: false },
    /** @type {Function} An optional function to set row background color. */
    rowColorRules: {
      type: Function,
      default: () => 'inherit',
    },
  },
  data() {
    return {
      /** @type {boolean} Whether the user is currently selecting rows */
      isSelecting: false,
      /** @type {boolean} The value to apply to selected rows */
      selectingValue: true,
      /** @type {Row[]} The list of rows of the table */
      rows: this.items.map((item, index) => new Row({ item, index })),
    };
  },
  computed: {
    /** @returns {string[]} The actual columns used by the table, derived from the properties of the first item if not specified as props */
    computedColumns() {
      return this.columns ?? Object.keys(this.items[0]);
    },
    /** @returns {number} The number of columns of the table */
    computedColumnsCount() {
      return this.computedColumns.length + (this.hideSelected ? 0 : 1);
    },
    /** @returns {Row[]} The list of rows, optionnally augmented with the drawer is there's a selection */
    rowsWithDrawer() {
      const rows = [...this.rows];
      const lastSelectedIndex = rows.findLastIndex(i => i.selected);
      if (lastSelectedIndex !== -1) {
        rows.splice(lastSelectedIndex + 1, 0, { type: 'drawer' });
      }
      return rows;
    },
  },
  watch: {
    /** Whenever the initial items change, recompute the rows. That will deselect any previously selected rows */
    items: {
      deep: true,
      handler() {
        this.rows = this.items.map((item, index) => new Row({ item, index }));
      },
    },
  },
  methods: {
    /**
     * Start the selection process upon mouse-downing a row.
     * @param {Row} row the row on which the user mouse-downed
     */
    startSelecting(row) {
      if (row.type === 'regular') {
        this.isSelecting = true;
        this.selectingValue = !row.selected;
        row.selected = this.selectingValue;
        this.$emit(
          'selectionChanged',
          this.rows.filter(r => r.selected)
        );
      }
    },
    /**
     * Select a row as the result of a mouse-move
     * @param {Row} row the row on which the user mouse-moved
     */
    selecting(row) {
      if (this.isSelecting && row.type === 'regular') {
        row.selected = this.selectingValue;
        this.$emit(
          'selectionChanged',
          this.rows.filter(r => r.selected)
        );
      }
    },
    /**
     * Stop selecting as the result of either a mouse-up on a row,
     * or a mouse-leave on the table.
     */
    stopSelecting() {
      this.isSelecting = false;
    },
  },
};
</script>

<style lang="scss">
.houston-table {
  display: grid;
  max-height: 50em;
  overflow-y: auto;
  user-select: none;
  cursor: pointer;
  > header[role='row'] {
    > div {
      background-color: white;
      position: sticky;
      font-weight: lighter;
      text-transform: uppercase;
      top: 0;
      font-size: smaller;
    }
  }

  > header[role='row'],
  div[role='row'] {
    display: contents;
    > div[role='cell'] {
      padding: 6px 4px 6px 4px;
      border-bottom: 1px solid $alt-200;
      white-space: nowrap;
      &:last-of-type {
        text-align: right;
      }
    }
  }

  &__header-cell {
    z-index: 1;
  }

  &__body {
    display: contents;
    > div[role='row'] {
      font-weight: bold;
      &.selected div {
        background-color: $brand-alt-50;
      }
    }
  }

  &__drawer {
    border: 1px solid $alt-200;
    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    background-color: $brand-alt-50;
    padding: $field-padding;
    cursor: initial;
    user-select: text;
    font-weight: normal;
  }
}

.v-grow-enter-active,
.v-grow-leave-active {
  transition-property: opacity, transform;
  transition: 0.3s ease-in-out;
  will-change: opacity, transform;

  & > div {
    transition: inherit;
    opacity: 1;
    transform: translate(0, 0);
  }
}

.v-grow-enter,
.v-grow-leave-to {
  & > div {
    opacity: 0;
    transform: translate(0, -2px);
  }
}
</style>
